import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';
import { dashboardAPI } from '../../services/api';
import { useBranchFilter } from '../../contexts/BranchFilterContext';
import { useAuth } from '../../contexts/AuthContext';
import ErrorModal from '../Common/ErrorModal';
import './Dashboard.css';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { selectedBranchId } = useBranchFilter();
  const { isSuperAdmin } = useAuth();
  const [stats, setStats] = useState({
    propertiesCount: 0,
    contractsCount: 0,
    organizationsCount: 0,
    branchesCount: 0,
    unitsCount: 0
  });
  const [chartsData, setChartsData] = useState({
    propertiesByStatus: [],
    propertiesByType: [],
    contractsByStatus: [],
    unitsByType: [],
    propertiesTrend: [],
    contractsTrend: []
  });
  const [recentData, setRecentData] = useState({
    properties: [],
    contracts: []
  });
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, [selectedBranchId]);

  // Get month abbreviations based on language
  const getMonthAbbreviations = () => {
    return [
      t('dashboard.months.jan'),
      t('dashboard.months.feb'),
      t('dashboard.months.mar'),
      t('dashboard.months.apr'),
      t('dashboard.months.may'),
      t('dashboard.months.jun'),
      t('dashboard.months.jul'),
      t('dashboard.months.aug'),
      t('dashboard.months.sep'),
      t('dashboard.months.oct'),
      t('dashboard.months.nov'),
      t('dashboard.months.dec')
    ];
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsResponse, chartsResponse, recentResponse] = await Promise.all([
        dashboardAPI.getStats(selectedBranchId),
        dashboardAPI.getCharts(selectedBranchId),
        dashboardAPI.getRecent(selectedBranchId)
      ]);
      
      setStats(statsResponse.data || {});
      setChartsData(chartsResponse.data || {});
      setRecentData(recentResponse.data || { properties: [], contracts: [] });
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setErrorMessage(t('dashboard.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const propertiesByStatusOptions = {
    series: chartsData.propertiesByStatus?.map(item => item.count) || [],
    chart: {
      type: 'donut',
      height: 350,
      fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '35%',
          labels: {
            show: true,
            total: {
              show: true,
              label: t('dashboard.stats.total'),
              fontSize: '18px',
              fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
              fontWeight: 700,
              color: '#2d3748',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => {
                  return a + b;
                }, 0);
              }
            }
          }
        }
      }
    },
    labels: chartsData.propertiesByStatus?.map(item => item.status) || [],
    colors: ['#bf4f1f', '#28a745', '#007bff', '#ffc107', '#dc3545', '#6f42c1'],
    legend: {
      position: 'bottom',
      fontSize: '14px',
      fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
      fontWeight: 500
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '12px',
        fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
        fontWeight: 600
      },
      dropShadow: {
        enabled: true,
        blur: 3,
        opacity: 0.3
      }
    },
    tooltip: {
      style: {
        fontSize: '12px',
        fontFamily: "'Inter', 'Poppins', sans-serif"
      }
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 300
        },
        legend: {
          position: 'bottom'
        }
      }
    }]
  };

  const propertiesByTypeOptions = {
    series: [{
      name: t('dashboard.charts.series.properties'),
      data: chartsData.propertiesByType?.map(item => item.count) || []
    }],
    chart: {
      type: 'bar',
      height: 350,
      fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        borderRadius: 8,
        borderRadiusApplication: 'end',
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '12px',
        fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
        fontWeight: 600
      }
    },
    xaxis: {
      categories: chartsData.propertiesByType?.map(item => item.type) || [],
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
          fontWeight: 500
        }
      }
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
          fontWeight: 500
        }
      }
    },
    colors: ['#bf4f1f'],
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.3,
        gradientToColors: ['#d4693e'],
        inverseColors: false,
        opacityFrom: 0.8,
        opacityTo: 0.9,
        stops: [0, 100]
      }
    },
    tooltip: {
      style: {
        fontSize: '12px',
        fontFamily: "'Inter', 'Poppins', sans-serif"
      }
    }
  };

  // Contracts by Status Chart Options
  const contractsByStatusOptions = {
    series: chartsData.contractsByStatus?.map(item => item.count) || [],
    chart: {
      type: 'donut',
      height: 350,
      fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '35%',
          labels: {
            show: true,
            total: {
              show: true,
              label: t('dashboard.stats.total'),
              fontSize: '18px',
              fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
              fontWeight: 700,
              color: '#2d3748',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => {
                  return a + b;
                }, 0);
              }
            }
          }
        }
      }
    },
    labels: chartsData.contractsByStatus?.map(item => item.status) || [],
    colors: ['#28a745', '#dc3545', '#007bff', '#ffc107'],
    legend: {
      position: 'bottom',
      fontSize: '14px',
      fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
      fontWeight: 500
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '12px',
        fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
        fontWeight: 600
      },
      dropShadow: {
        enabled: true,
        blur: 3,
        opacity: 0.3
      }
    },
    tooltip: {
      style: {
        fontSize: '12px',
        fontFamily: "'Inter', 'Poppins', sans-serif"
      }
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 300
        },
        legend: {
          position: 'bottom'
        }
      }
    }]
  };

  // Units by Type Chart Options
  const unitsByTypeOptions = {
    series: [{
      name: 'Units',
      data: chartsData.unitsByType?.map(item => item.count) || []
    }],
    chart: {
      type: 'bar',
      height: 350,
      fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        borderRadius: 8,
        borderRadiusApplication: 'end',
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '12px',
        fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
        fontWeight: 600
      }
    },
    xaxis: {
      categories: chartsData.unitsByType?.map(item => item.type) || [],
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
          fontWeight: 500
        }
      }
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
          fontWeight: 500
        }
      }
    },
    colors: ['#6f42c1'],
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.3,
        gradientToColors: ['#8b5fcf'],
        inverseColors: false,
        opacityFrom: 0.8,
        opacityTo: 0.9,
        stops: [0, 100]
      }
    },
    tooltip: {
      style: {
        fontSize: '12px',
        fontFamily: "'Inter', 'Poppins', sans-serif"
      }
    }
  };

  // Contracts Trend Chart Options
  const contractsTrendOptions = {
    series: [{
      name: t('dashboard.charts.series.contracts'),
      data: chartsData.contractsTrend?.map(item => item.count) || []
    }],
    chart: {
      type: 'area',
      height: 350,
      fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      zoom: {
        enabled: true
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth',
      width: 3,
      lineCap: 'round'
    },
    colors: ['#28a745'],
    xaxis: {
      categories: chartsData.contractsTrend?.map(item => item.month) || getMonthAbbreviations(),
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
          fontWeight: 500
        }
      }
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
          fontWeight: 500
        }
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.3,
        gradientToColors: ['#34ce57'],
        inverseColors: false,
        opacityFrom: 0.6,
        opacityTo: 0.1,
        stops: [0, 100]
      }
    },
    markers: {
      size: 5,
      colors: ['#28a745'],
      strokeWidth: 2,
      strokeColors: '#fff',
      hover: {
        size: 7
      }
    },
    tooltip: {
      style: {
        fontSize: '12px',
        fontFamily: "'Inter', 'Poppins', sans-serif"
      },
      theme: 'light'
    },
    grid: {
      borderColor: '#e2e8f0',
      strokeDashArray: 4
    }
  };

  // Properties Trend Chart Options
  const propertiesTrendOptions = {
    series: [{
      name: t('dashboard.charts.series.properties'),
      data: chartsData.propertiesTrend?.map(item => item.count) || []
    }],
    chart: {
      type: 'line',
      height: 350,
      fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      zoom: {
        enabled: true
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth',
      width: 3,
      lineCap: 'round'
    },
    colors: ['#bf4f1f'],
    xaxis: {
      categories: chartsData.propertiesTrend?.map(item => item.month) || getMonthAbbreviations(),
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
          fontWeight: 500
        }
      }
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: i18n.language === 'ar' ? "'Cairo', sans-serif" : "'Inter', 'Poppins', sans-serif",
          fontWeight: 500
        }
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.3,
        gradientToColors: ['#d4693e'],
        inverseColors: false,
        opacityFrom: 0.6,
        opacityTo: 0.1,
        stops: [0, 100]
      }
    },
    markers: {
      size: 5,
      colors: ['#bf4f1f'],
      strokeWidth: 2,
      strokeColors: '#fff',
      hover: {
        size: 7
      }
    },
    tooltip: {
      style: {
        fontSize: '12px',
        fontFamily: "'Inter', 'Poppins', sans-serif"
      },
      theme: 'light'
    },
    grid: {
      borderColor: '#e2e8f0',
      strokeDashArray: 4
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
          <p>{t('dashboard.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />

      {/* Page Header */}
      <div className="dashboard-header">
        <h1>{t('dashboard.title')}</h1>
      </div>

      {/* Stat Cards Section */}
      <div className="dashboard-stats-grid">
        <div 
          className="stat-card stat-card-primary" 
          onClick={() => navigate('/property')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/property');
            }
          }}
        >
          <div className="stat-card-content">
            <div className="stat-icon-wrapper">
              <div className="stat-icon">
                <i className="fas fa-building"></i>
              </div>
            </div>
            <div className="stat-info">
              <h3 className="stat-value">{stats.propertiesCount || 0}</h3>
              <p className="stat-label">{t('dashboard.stats.properties')}</p>
            </div>
            <div className="stat-trend">
              <i className="fas fa-arrow-up"></i>
              <span>{t('dashboard.stats.total')}</span>
            </div>
          </div>
        </div>

        <div 
          className="stat-card stat-card-success" 
          onClick={() => navigate('/contract')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/contract');
            }
          }}
        >
          <div className="stat-card-content">
            <div className="stat-icon-wrapper">
              <div className="stat-icon">
                <i className="fas fa-file-contract"></i>
              </div>
            </div>
            <div className="stat-info">
              <h3 className="stat-value">{stats.contractsCount || 0}</h3>
              <p className="stat-label">{t('dashboard.stats.contracts')}</p>
            </div>
            <div className="stat-trend">
              <i className="fas fa-arrow-up"></i>
              <span>{t('dashboard.stats.total')}</span>
            </div>
          </div>
        </div>

        {isSuperAdmin ? (
          <div 
            className="stat-card stat-card-info" 
            onClick={() => navigate('/organization')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/organization');
              }
            }}
          >
            <div className="stat-card-content">
              <div className="stat-icon-wrapper">
                <div className="stat-icon">
                  <i className="fas fa-sitemap"></i>
                </div>
              </div>
              <div className="stat-info">
                <h3 className="stat-value">{stats.organizationsCount || 0}</h3>
                <p className="stat-label">{t('dashboard.stats.organizations')}</p>
              </div>
              <div className="stat-trend">
                <i className="fas fa-arrow-up"></i>
                <span>{t('dashboard.stats.total')}</span>
              </div>
            </div>
          </div>
        ) : (
          <div 
            className="stat-card stat-card-info" 
            onClick={() => navigate('/branch')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/branch');
              }
            }}
          >
            <div className="stat-card-content">
              <div className="stat-icon-wrapper">
                <div className="stat-icon">
                  <i className="fas fa-code-branch"></i>
                </div>
              </div>
              <div className="stat-info">
                <h3 className="stat-value">{stats.branchesCount || 0}</h3>
                <p className="stat-label">{t('dashboard.stats.branches')}</p>
              </div>
              <div className="stat-trend">
                <i className="fas fa-arrow-up"></i>
                <span>{t('dashboard.stats.total')}</span>
              </div>
            </div>
          </div>
        )}

        <div 
          className="stat-card stat-card-purple" 
          onClick={() => navigate('/property')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/property');
            }
          }}
        >
          <div className="stat-card-content">
            <div className="stat-icon-wrapper">
              <div className="stat-icon">
                <i className="fas fa-cube"></i>
              </div>
            </div>
            <div className="stat-info">
              <h3 className="stat-value">{stats.unitsCount || 0}</h3>
              <p className="stat-label">{t('dashboard.stats.units')}</p>
            </div>
            <div className="stat-trend">
              <i className="fas fa-arrow-up"></i>
              <span>{t('dashboard.stats.total')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="dashboard-charts-grid">
        {/* Properties by Status - Pie Chart */}
        <div className="chart-card">
          <div className="chart-card-header">
            <div className="chart-title">
              <i className="fas fa-chart-pie"></i>
              <span>{t('dashboard.charts.propertiesByStatus')}</span>
            </div>
          </div>
          <div className="chart-card-body">
            {chartsData.propertiesByStatus?.length > 0 ? (
              <Chart
                options={propertiesByStatusOptions}
                series={propertiesByStatusOptions.series}
                type="donut"
                height={350}
              />
            ) : (
              <div className="chart-empty-state">
                <i className="fas fa-chart-pie"></i>
                <p>{t('dashboard.charts.noData')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Contracts by Status - Pie Chart */}
        <div className="chart-card">
          <div className="chart-card-header">
            <div className="chart-title">
              <i className="fas fa-chart-pie"></i>
              <span>{t('dashboard.charts.contractsByStatus')}</span>
            </div>
          </div>
          <div className="chart-card-body">
            {chartsData.contractsByStatus?.length > 0 ? (
              <Chart
                options={contractsByStatusOptions}
                series={contractsByStatusOptions.series}
                type="donut"
                height={350}
              />
            ) : (
              <div className="chart-empty-state">
                <i className="fas fa-chart-pie"></i>
                <p>{t('dashboard.charts.noData')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Properties Trend - Line Chart */}
        <div className="chart-card">
          <div className="chart-card-header">
            <div className="chart-title">
              <i className="fas fa-chart-line"></i>
              <span>{t('dashboard.charts.propertiesTrend')}</span>
            </div>
          </div>
          <div className="chart-card-body">
            {chartsData.propertiesTrend?.length > 0 ? (
              <Chart
                options={propertiesTrendOptions}
                series={propertiesTrendOptions.series}
                type="line"
                height={350}
              />
            ) : (
              <div className="chart-empty-state">
                <i className="fas fa-chart-line"></i>
                <p>{t('dashboard.charts.noData')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Units by Type - Bar Chart */}
        <div className="chart-card">
          <div className="chart-card-header">
            <div className="chart-title">
              <i className="fas fa-chart-bar"></i>
              <span>{t('dashboard.charts.unitsByType')}</span>
            </div>
          </div>
          <div className="chart-card-body">
            {chartsData.unitsByType?.length > 0 ? (
              <Chart
                options={unitsByTypeOptions}
                series={unitsByTypeOptions.series}
                type="bar"
                height={350}
              />
            ) : (
              <div className="chart-empty-state">
                <i className="fas fa-chart-bar"></i>
                <p>{t('dashboard.charts.noData')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Contracts Trend - Area Chart */}
        <div className="chart-card">
          <div className="chart-card-header">
            <div className="chart-title">
              <i className="fas fa-chart-area"></i>
              <span>{t('dashboard.charts.contractsTrend')}</span>
            </div>
          </div>
          <div className="chart-card-body">
            {chartsData.contractsTrend?.length > 0 ? (
              <Chart
                options={contractsTrendOptions}
                series={contractsTrendOptions.series}
                type="area"
                height={350}
              />
            ) : (
              <div className="chart-empty-state">
                <i className="fas fa-chart-area"></i>
                <p>{t('dashboard.charts.noData')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Properties by Type - Bar Chart */}
        <div className="chart-card">
          <div className="chart-card-header">
            <div className="chart-title">
              <i className="fas fa-chart-bar"></i>
              <span>{t('dashboard.charts.propertiesByType')}</span>
            </div>
          </div>
          <div className="chart-card-body">
            {chartsData.propertiesByType?.length > 0 ? (
              <Chart
                options={propertiesByTypeOptions}
                series={propertiesByTypeOptions.series}
                type="bar"
                height={350}
              />
            ) : (
              <div className="chart-empty-state">
                <i className="fas fa-chart-bar"></i>
                <p>{t('dashboard.charts.noData')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Items Section */}
      <div className="dashboard-recent-grid">
        {/* Recent Properties */}
        <div className="recent-card">
          <div className="recent-card-header">
            <div className="recent-title">
              <i className="fas fa-building"></i>
              <span>{t('dashboard.recent.properties')}</span>
            </div>
            <a href="/property" className="recent-view-all">{t('dashboard.recent.viewAll')}</a>
          </div>
          <div className="recent-card-body">
            {recentData.properties?.length > 0 ? (
              <ul className="recent-list">
                {recentData.properties.map((property) => (
                  <li key={property.id} className="recent-item">
                    <div className="recent-item-icon">
                      <i className="fas fa-building"></i>
                    </div>
                    <div className="recent-item-content">
                      <h4 className="recent-item-title">{property.nameEng || property.nameAr || 'Unnamed'}</h4>
                      <p className="recent-item-subtitle">
                        <span className="recent-item-code">{property.code}</span>
                        <span className="recent-item-date">
                          {new Date(property.createdAt).toLocaleDateString()}
                        </span>
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="recent-empty-state">
                <i className="fas fa-building"></i>
                <p>{t('dashboard.recent.noProperties')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Contracts */}
        <div className="recent-card">
          <div className="recent-card-header">
            <div className="recent-title">
              <i className="fas fa-file-contract"></i>
              <span>{t('dashboard.recent.contracts')}</span>
            </div>
            <a href="/contract" className="recent-view-all">{t('dashboard.recent.viewAll')}</a>
          </div>
          <div className="recent-card-body">
            {recentData.contracts?.length > 0 ? (
              <ul className="recent-list">
                {recentData.contracts.map((contract) => (
                  <li key={contract.id} className="recent-item">
                    <div className="recent-item-icon">
                      <i className="fas fa-file-contract"></i>
                    </div>
                    <div className="recent-item-content">
                      <h4 className="recent-item-title">
                        {contract.propertyNameEng || contract.propertyNameAr || t('dashboard.recent.unnamedProperty')}
                      </h4>
                      <p className="recent-item-subtitle">
                        <span className={`recent-item-status ${contract.isActive ? 'status-active' : 'status-inactive'}`}>
                          {contract.isActive ? t('common.active') : t('common.inactive')}
                        </span>
                        <span className="recent-item-date">
                          {new Date(contract.createdAt).toLocaleDateString()}
                        </span>
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="recent-empty-state">
                <i className="fas fa-file-contract"></i>
                <p>{t('dashboard.recent.noContracts')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

